import {
  actionCreators as assignmentFormActions,
  saveAssignmentFormSaga,
} from '@app/components/AssignmentForm/modules/assignmentForm'
import { actionCreators as fileUploadActions } from '@app/components/FileUpload/modules/fileUpload'
import { FeedItemSourceType, LeadPriority, LeadType } from '@app/models/enums'
import { FeedItem } from '@app/models/FeedItem'
import { ApplicationState } from '@app/store/index'
import { convertToMarkup } from '@app/utils/mention'
import { errorToast, successToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { EditorState } from 'draft-js'
import { Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { call, fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector, createStructuredSelector } from 'reselect'

// -----------------
// STATE - This defines the type of data maintained in the Redux store.
export interface IFeedItemEditorState {
  readonly attemptingCreate: boolean
  readonly error?: string
  readonly feedItemType: string
  readonly fileInfo: File
  readonly isAssignmentModalOpen: boolean
  readonly leadPriority: LeadPriority
  readonly leadType: LeadType
  readonly makePublic: boolean
  readonly postText: EditorState
  readonly selectedTab: number
  readonly titleText: string
}

// initial state
const FeedItemEditorStateRecord = Record({
  attemptingCreate: false,
  error: undefined,
  feedItemType: 'Post',
  fileInfo: undefined,
  isAssignmentModalOpen: false,
  leadPriority: 'Medium',
  leadType: 'Other',
  makePublic: true,
  postText: EditorState.createEmpty(),
  selectedTab: 1,
  titleText: '',
})

export class FeedItemEditorState extends FeedItemEditorStateRecord {
  public static fromJS(initialState?: Partial<IFeedItemEditorState>) {
    return new FeedItemEditorState(initialState)
  }

  public readonly attemptingCreate: boolean
  public readonly error?: string
  public readonly feedItemType: string
  public readonly fileInfo: File
  public readonly isAssignmentModalOpen: boolean
  public readonly leadPriority: LeadPriority
  public readonly leadType: LeadType
  public readonly makePublic: boolean
  public readonly postText: EditorState
  public readonly selectedTab: number
  public readonly titleText: string

  constructor(props?: Partial<FeedItemEditorState>) {
    props ? super(props) : super()
  }
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
// Use @typeName and isActionType for type detection that works even after serialization/deserialization.
export enum FeedItemEditorActionTypes {
  AttemptCreateAssignmentForm = 'FEED_ITEM_EDITOR::ATTEMPT_CREATE_ASSIGNMENT_FORM_ACTION',
  SetSelectedTab = 'FEED_ITEM_EDITOR::SET_SELECTED_TAB',
  ToggleFeedItemPublic = 'FEED_ITEM_EDITOR::TOGGLE_FEED_ITEM_PUBLIC',
  SetTitleText = 'FEED_ITEM_EDITOR::SET_TITLE_TEXT',
  SetPostText = 'FEED_ITEM_EDITOR::SET_POST_TEXT',
  SetFileUploadInfo = 'FEED_ITEM_EDITOR::SET_FILE_UPLOAD_INFO',
  SetLeadPriority = 'FEED_ITEM_EDITOR::SET_LEAD_PRIORITY',
  SetLeadType = 'FEED_ITEM_EDITOR::SET_LEAD_TYPE',
  CreateFeedItemRequest = 'FEED_ITEM_EDITOR::CREATE_FEED_ITEM_REQUEST',
  CreateFeedItemFailure = 'FEED_ITEM_EDITOR:CREATE_FEED_ITEM_FAILURE',
  CreateFeedItemSuccess = 'FEED_ITEM_EDITOR::CREATE_FEED_ITEM_SUCCESS',
  AttemptCreateFeedItem = 'FEED_ITEM_EDITOR::ATTEMPT_CREATE_FEED_ITEM',
  ToggleAssignmentModal = 'FEED_ITEM_EDITOR::TOGGLE_ASSIGNMENT_MODAL',
}

export interface IAttemptCreateAssignmentFormAction {
  type: FeedItemEditorActionTypes.AttemptCreateAssignmentForm
}
export interface ISetSelectedTabAction {
  type: FeedItemEditorActionTypes.SetSelectedTab
  tabIndex: number
}
export interface IToggleFeedItemPublicAction {
  type: FeedItemEditorActionTypes.ToggleFeedItemPublic
}
export interface ISetTitleTextAction {
  type: FeedItemEditorActionTypes.SetTitleText
  text: string
}
export interface ISetPostTextAction {
  type: FeedItemEditorActionTypes.SetPostText
  editorState: EditorState
}
export interface ISetFileUploadInfoAction {
  type: FeedItemEditorActionTypes.SetFileUploadInfo
  fileInfo: File
}
export interface ISetLeadPriorityAction {
  type: FeedItemEditorActionTypes.SetLeadPriority
  value: string
}
export interface ISetLeadTypeAction {
  type: FeedItemEditorActionTypes.SetLeadType
  value: string
}
export interface ICreateFeedItemRequestAction {
  type: FeedItemEditorActionTypes.CreateFeedItemRequest
}
export interface ICreateFeedItemFailureAction {
  type: FeedItemEditorActionTypes.CreateFeedItemFailure
  error: string
}
export interface ICreateFeedItemSuccessAction {
  type: FeedItemEditorActionTypes.CreateFeedItemSuccess
  feedItem: FeedItem
}
export interface IAttemptCreateFeedItemAction {
  contextId?: string
  contextType?: FeedItemSourceType
  type: FeedItemEditorActionTypes.AttemptCreateFeedItem
}
export interface IToggleAssignmentModalAction {
  type: FeedItemEditorActionTypes.ToggleAssignmentModal
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ISetSelectedTabAction
  | IToggleFeedItemPublicAction
  | ISetTitleTextAction
  | ISetPostTextAction
  | ISetFileUploadInfoAction
  | ISetLeadPriorityAction
  | ISetLeadTypeAction
  | ICreateFeedItemFailureAction
  | ICreateFeedItemRequestAction
  | ICreateFeedItemSuccessAction
  | IToggleAssignmentModalAction

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).
function attemptCreateAssignmentForm(): IAttemptCreateAssignmentFormAction {
  return {
    type: FeedItemEditorActionTypes.AttemptCreateAssignmentForm,
  }
}

function attemptCreateFeedItem(
  contextId?: string,
  contextType?: FeedItemSourceType,
): IAttemptCreateFeedItemAction {
  return {
    contextId,
    contextType,
    type: FeedItemEditorActionTypes.AttemptCreateFeedItem,
  }
}

function createFeedItemRequest(): ICreateFeedItemRequestAction {
  return {
    type: FeedItemEditorActionTypes.CreateFeedItemRequest,
  }
}

function createFeedItemFailure(error: string): ICreateFeedItemFailureAction {
  return {
    error,
    type: FeedItemEditorActionTypes.CreateFeedItemFailure,
  }
}

function createFeedItemSuccess(
  feedItem: FeedItem,
): ICreateFeedItemSuccessAction {
  return {
    feedItem,
    type: FeedItemEditorActionTypes.CreateFeedItemSuccess,
  }
}

function toggleAssignmentModal(): IToggleAssignmentModalAction {
  return {
    type: FeedItemEditorActionTypes.ToggleAssignmentModal,
  }
}

export const actionCreators = {
  attemptCreateAssignmentForm,
  attemptCreateFeedItem,
  setFileUploadInfo: (fileInfo: File): ISetFileUploadInfoAction => ({
    fileInfo,
    type: FeedItemEditorActionTypes.SetFileUploadInfo,
  }),
  setLeadPriority: (value: string): ISetLeadPriorityAction => ({
    type: FeedItemEditorActionTypes.SetLeadPriority,
    value,
  }),
  setLeadType: (value: string): ISetLeadTypeAction => ({
    type: FeedItemEditorActionTypes.SetLeadType,
    value,
  }),
  setPostText: (editorState: EditorState): ISetPostTextAction => ({
    editorState,
    type: FeedItemEditorActionTypes.SetPostText,
  }),
  setSelectedTab: (tabIndex: number): ISetSelectedTabAction => ({
    tabIndex,
    type: FeedItemEditorActionTypes.SetSelectedTab,
  }),
  setTitleText: (text: string): ISetTitleTextAction => ({
    text,
    type: FeedItemEditorActionTypes.SetTitleText,
  }),
  toggleAssignmentModal,
  togglePublic: (): IToggleFeedItemPublicAction => ({
    type: FeedItemEditorActionTypes.ToggleFeedItemPublic,
  }),
}

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
export const reducer: Reducer<FeedItemEditorState> = (
  state: FeedItemEditorState = new FeedItemEditorState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction

  switch (action.type) {
    case FeedItemEditorActionTypes.SetSelectedTab:
      return state
        .set('selectedTab', action.tabIndex)
        .set(
          'feedItemType',
          action.tabIndex === 1
            ? 'Post'
            : action.tabIndex === 2
            ? 'Lead'
            : 'Assignment',
        ) as FeedItemEditorState
    case FeedItemEditorActionTypes.ToggleFeedItemPublic:
      return state.set('makePublic', !state.makePublic) as FeedItemEditorState
    case FeedItemEditorActionTypes.SetLeadPriority:
      return state.set('leadPriority', action.value) as FeedItemEditorState
    case FeedItemEditorActionTypes.SetLeadType:
      return state.set('leadType', action.value) as FeedItemEditorState
    case FeedItemEditorActionTypes.SetTitleText:
      return state.set('titleText', action.text) as FeedItemEditorState
    case FeedItemEditorActionTypes.SetPostText:
      return state.set('postText', action.editorState) as FeedItemEditorState
    case FeedItemEditorActionTypes.SetFileUploadInfo:
      return state.set('fileInfo', action.fileInfo) as FeedItemEditorState
    case FeedItemEditorActionTypes.CreateFeedItemRequest:
      return state.set('attemptingCreate', true) as FeedItemEditorState
    case FeedItemEditorActionTypes.CreateFeedItemFailure:
      return state
        .set('error', action.error)
        .set('attemptingCreate', false) as FeedItemEditorState
    case FeedItemEditorActionTypes.CreateFeedItemSuccess:
      return state
        .set('error', undefined)
        .set('attemptingCreate', false)
        .set('postText', EditorState.createEmpty()) as FeedItemEditorState
    case FeedItemEditorActionTypes.ToggleAssignmentModal:
      return state
        .set('isAssignmentModalOpen', !state.isAssignmentModalOpen)
        .set(
          'selectedTab',
          !state.isAssignmentModalOpen ? 1 : state.selectedTab,
        )
        .set('feedItemType', 'Post') as FeedItemEditorState
    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  // For unrecognized actions (or in cases where actions have no effect), must return the existing state
  //  (or default initial state if none was supplied)
  return state
}

// -------------------------
// SAGAS
// -------------------------
function* attemptCreateFeedItemSaga(action: IAttemptCreateFeedItemAction) {
  try {
    yield put(createFeedItemRequest())

    const state: FeedItemEditorState = yield select(selectModuleState)

    // get attachments and map to array of objects
    let attachments = yield select(selectAttachments())
    if (attachments) {
      attachments = attachments.valueSeq().map((item: any) => {
        if (!item.error) {
          return item.attachment
        }

        return false
      })
    } else {
      attachments = []
    }

    const feedItemOpts = {
      body: JSON.stringify({
        attachments,
        isPublic: state.makePublic,
        leadPriority: state.leadPriority,
        leadType: state.leadType,
        markup: convertToMarkup(state.postText.getCurrentContent()),
        title: state.titleText,
        type: state.feedItemType,
      }),
      method: RequestMethod.POST,
    }

    let url = '/api/FeedItems'

    if (action.contextType && action.contextId) {
      url += `/${action.contextType}/${action.contextId}`
    }

    const result = yield makeAuthenticatedRequest(url, feedItemOpts)

    if (result.success) {
      yield put(
        successToast('Success', `Feed Item ${state.feedItemType} Created`),
      )

      yield put(createFeedItemSuccess(FeedItem.fromJS(result.entity)))

      yield put(fileUploadActions.clearFileList('feed-item-editor'))
    } else {
      yield put(errorToast('Oops!', result.message))
      yield put(createFeedItemFailure(result.message))
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
    yield put(createFeedItemFailure(err.message))
  }
}

function* attemptCreateAssignmentFormSaga(
  action: IAttemptCreateAssignmentFormAction,
) {
  const result = yield call(
    saveAssignmentFormSaga,
    assignmentFormActions.saveAssignmentForm(),
  )
  yield put(toggleAssignmentModal())
}

function* watchAttemptCreateFeedItem() {
  yield takeLatest(
    FeedItemEditorActionTypes.AttemptCreateFeedItem,
    attemptCreateFeedItemSaga,
  )
}

function* watchAttemptCreateAssignmentForm() {
  yield takeLatest(
    FeedItemEditorActionTypes.AttemptCreateAssignmentForm,
    attemptCreateAssignmentFormSaga,
  )
}

export function* feedItemEditorRootSaga() {
  yield fork(watchAttemptCreateFeedItem)
  yield fork(watchAttemptCreateAssignmentForm)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = (state: ApplicationState) => state.feedItemEditor
const selectFileUploadState = (state: ApplicationState) => state.fileUpload

export const selectAttachments = () =>
  createSelector(
    selectFileUploadState,
    (mState) => {
      return mState.files.get('feed-item-editor')
    },
  )

export const selectSelectedTab = () =>
  createSelector(
    selectModuleState,
    (mState) => mState.selectedTab,
  )

export const selectLeadPriority = () =>
  createSelector(
    selectModuleState,
    (mState) => mState.leadPriority,
  )

export const selectLeadType = () =>
  createSelector(
    selectModuleState,
    (mState) => mState.leadType,
  )

export const selectMakePublic = () =>
  createSelector(
    selectModuleState,
    (mState) => mState.makePublic,
  )

export const selectTitleText = () =>
  createSelector(
    selectModuleState,
    (mState) => mState.titleText,
  )

export const selectPostText = () =>
  createSelector(
    selectModuleState,
    (mState) => mState.postText,
  )

export const selectFileInfo = () =>
  createSelector(
    selectModuleState,
    (mState) => mState.fileInfo,
  )

export const selectError = () =>
  createSelector(
    selectModuleState,
    (mState) => mState.error,
  )

export const selectAttemptingCreate = () =>
  createSelector(
    selectModuleState,
    (mState) => mState.attemptingCreate,
  )

export const selectFeedItemType = () =>
  createSelector(
    selectModuleState,
    (mState) => mState.feedItemType,
  )

export const selectIsAssignmentModalOpen = () =>
  createSelector(
    selectModuleState,
    (mState) => mState.isAssignmentModalOpen,
  )

export const selectFeedItemEditorState = createStructuredSelector<
  ApplicationState,
  IFeedItemEditorState
>({
  attemptingCreate: selectAttemptingCreate(),
  error: selectError(),
  feedItemType: selectFeedItemType(),
  fileInfo: selectFileInfo(),
  isAssignmentModalOpen: selectIsAssignmentModalOpen(),
  leadPriority: selectLeadPriority(),
  leadType: selectLeadType(),
  makePublic: selectMakePublic(),
  postText: selectPostText(),
  selectedTab: selectSelectedTab(),
  titleText: selectTitleText(),
})
